import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  ActivitiesApiException,
  ContractInformationTaskSyncErrorReason,
  ContractInformationTaskSyncIssueKind,
  type ContractInformationTaskConflict,
  type ContractInformationTaskSyncError,
  type ContractInformationTaskSyncIssue,
  EnsureContractInformationTasksRequestScope,
  type EnsureContractInformationTasksResponse,
  EnsureContractInformationTasksResponseOutcome,
  type PreviewContractInformationTaskConflictsRequest,
  PreviewContractInformationTaskConflictsRequestScope,
  useEnsureContractInformationTasks,
  useInvalidateUseGetAutopilotTasksForCampaign,
  usePreviewContractInformationTaskConflicts,
} from '@urbanx/agx.activities.client';
import {
  subscribeToTaskCollectionReconnect,
  subscribeToTaskCollectionRefresh,
} from 'hooks/taskCollectionRefresh';
import { useAutopilotTaskUpdates } from 'hooks/useAutopilotTaskUpdates';
import type { TaskUpdatedNotification } from 'types/pushNotifications';
import type {
  ContractInformationSynchronizationIssue,
  ContractInformationSynchronizationPolicy,
  ContractInformationSynchronizationResult,
} from './useContractInformationEditor';

const transientRetryDelays = [0, 1_000, 3_000] as const;

interface UseContractInformationTaskSynchronizationOptions {
  campaignId: string | undefined;
  contractId: string | undefined;
  scope: EnsureContractInformationTasksRequestScope;
  isTaskWorkflowEnabled: boolean;
  canonicalRevision?: string;
}

interface SynchronizationState {
  resourceKey: string;
  status: ContractInformationSynchronizationPolicy['status'];
  issue?: ContractInformationSynchronizationIssue;
  isCoolingOffTaskCompleted?: boolean;
}

interface EnsureQueue {
  resourceKey: string;
  running?: Promise<ContractInformationSynchronizationResult>;
  trailing?: Promise<ContractInformationSynchronizationResult>;
}

type ContractInformationTaskSyncFailure = ContractInformationTaskSyncError & {
  isCoolingOffTaskCompleted: boolean;
  issues: ContractInformationTaskSyncIssue[];
};

const wait = (milliseconds: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, milliseconds);
  });

const readProperty = (value: object, property: string): unknown => Reflect.get(value, property);

const isContractInformationTaskSyncIssueKind = (
  value: unknown
): value is ContractInformationTaskSyncIssueKind => {
  switch (value) {
    case ContractInformationTaskSyncIssueKind.MissingTask:
    case ContractInformationTaskSyncIssueKind.UnexpectedTask:
    case ContractInformationTaskSyncIssueKind.DueDateMismatch:
    case ContractInformationTaskSyncIssueKind.DependencyMismatch:
    case ContractInformationTaskSyncIssueKind.DisplayTitleMismatch:
    case ContractInformationTaskSyncIssueKind.WorkflowPlacementMismatch:
    case ContractInformationTaskSyncIssueKind.MissingContractDate:
      return true;
    default:
      return false;
  }
};

const isContractInformationTaskSyncError = (
  error: unknown
): error is ContractInformationTaskSyncFailure => {
  if (typeof error !== 'object' || error === null) return false;

  const reason = readProperty(error, 'reason');
  const issues = readProperty(error, 'issues');
  return (
    (reason === ContractInformationTaskSyncErrorReason.CanonicalPrerequisiteMissing ||
      reason === ContractInformationTaskSyncErrorReason.RepairDidNotConverge) &&
    typeof readProperty(error, 'isCoolingOffTaskCompleted') === 'boolean' &&
    Array.isArray(issues) &&
    issues.length > 0 &&
    issues.every(
      (issue) =>
        typeof issue === 'object' &&
        issue !== null &&
        typeof readProperty(issue, 'taskId') === 'string' &&
        typeof readProperty(issue, 'taskType') === 'string' &&
        typeof readProperty(issue, 'displayName') === 'string' &&
        isContractInformationTaskSyncIssueKind(readProperty(issue, 'kind'))
    )
  );
};

const isTransientError = (error: unknown) => {
  if (isContractInformationTaskSyncError(error)) return false;
  if (typeof error !== 'object' || error === null) return false;

  if (ActivitiesApiException.isActivitiesApiException(error)) {
    return error.status === 408 || error.status === 429 || error.status >= 500;
  }

  return error instanceof Error;
};

const requireCoolingOffCompletion = (
  response: EnsureContractInformationTasksResponse | ContractInformationTaskSyncError
) => {
  if (typeof response.isCoolingOffTaskCompleted !== 'boolean') {
    throw new Error('Cooling Off task completion state is required');
  }

  return response.isCoolingOffTaskCompleted;
};

const describeIssue = (issue: ContractInformationTaskSyncIssue) => {
  const reason = (() => {
    switch (issue.kind) {
      case ContractInformationTaskSyncIssueKind.MissingTask:
        return 'the related task is missing';
      case ContractInformationTaskSyncIssueKind.UnexpectedTask:
        return 'the related task should be removed';
      case ContractInformationTaskSyncIssueKind.DueDateMismatch:
        return 'the due date does not match the contract';
      case ContractInformationTaskSyncIssueKind.DependencyMismatch:
        return 'the task dependencies do not match the contract';
      case ContractInformationTaskSyncIssueKind.DisplayTitleMismatch:
        return 'the task name does not match the contract';
      case ContractInformationTaskSyncIssueKind.WorkflowPlacementMismatch:
        return 'the task is in the wrong workflow position';
      case ContractInformationTaskSyncIssueKind.MissingContractDate:
        return 'add a Contract Date or remove the cooling-off information';
    }
  })();

  return `${issue.displayName}: ${reason}.`;
};

const presentSynchronizationError = (
  error: ContractInformationTaskSyncFailure,
  scope: EnsureContractInformationTasksRequestScope
) => {
  const details = error.issues.map(describeIssue);
  const correctionRequired =
    error.reason === ContractInformationTaskSyncErrorReason.CanonicalPrerequisiteMissing &&
    scope === EnsureContractInformationTasksRequestScope.DatesAndConditions;

  return {
    result: {
      status: correctionRequired ? ('correction-required' as const) : ('unresolved' as const),
    },
    issue: {
      kind: correctionRequired ? ('correction-required' as const) : ('unresolved' as const),
      message: correctionRequired
        ? 'A Contract Date is required before related Cooling Off tasks can be updated.'
        : error.reason === ContractInformationTaskSyncErrorReason.CanonicalPrerequisiteMissing
          ? 'Add a Contract Date in Conditions before related tasks can be updated.'
          : "Some related tasks couldn't be updated.",
      details,
    },
    isCoolingOffTaskCompleted: requireCoolingOffCompletion(error),
  };
};

const unverifiedResult = {
  result: { status: 'unverified' as const },
  issue: {
    kind: 'unverified' as const,
    message: "We couldn't verify related tasks. We'll try again when you save.",
    details: [],
  },
};

export const useContractInformationTaskSynchronization = ({
  campaignId,
  contractId,
  scope,
  isTaskWorkflowEnabled,
  canonicalRevision,
}: UseContractInformationTaskSynchronizationOptions) => {
  const enabled = Boolean(campaignId && contractId && isTaskWorkflowEnabled);
  const resourceKey = `${campaignId ?? ''}:${contractId ?? ''}:${scope}`;
  const [state, setState] = useState<SynchronizationState>({
    resourceKey,
    status: enabled ? 'ensuring' : 'ready',
    isCoolingOffTaskCompleted: enabled ? undefined : false,
  });
  const currentResourceKey = useRef(resourceKey);
  const mounted = useRef(true);
  const queue = useRef<EnsureQueue>({ resourceKey });
  const requestEnsureRef = useRef<
    ((fresh: boolean) => Promise<ContractInformationSynchronizationResult>) | undefined
  >(undefined);
  const invalidateTasks = useInvalidateUseGetAutopilotTasksForCampaign();
  const { previewContractInformationTaskConflicts } = usePreviewContractInformationTaskConflicts({
    onSuccess: () => {},
    onError: () => {},
  });
  const { ensureContractInformationTasks } = useEnsureContractInformationTasks({
    onSuccess: () => {},
    onError: () => {},
  });

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useLayoutEffect(() => {
    if (currentResourceKey.current === resourceKey) return;

    currentResourceKey.current = resourceKey;
    queue.current = { resourceKey };
    setState({
      resourceKey,
      status: enabled ? 'ensuring' : 'ready',
      isCoolingOffTaskCompleted: enabled ? undefined : false,
    });
  }, [enabled, resourceKey]);

  const requireResource = useCallback(() => {
    if (!isTaskWorkflowEnabled) throw new Error('Task workflow is not enabled');
    if (!campaignId || !contractId) throw new Error('Campaign and contract are required');

    return { campaignId, contractId };
  }, [campaignId, contractId, isTaskWorkflowEnabled]);

  const previewConflicts = useCallback(
    async (
      proposal: Omit<
        PreviewContractInformationTaskConflictsRequest,
        'campaignId' | 'contractId' | 'scope'
      >
    ): Promise<ContractInformationTaskConflict[]> => {
      const response = await previewContractInformationTaskConflicts({
        ...proposal,
        ...requireResource(),
        scope: PreviewContractInformationTaskConflictsRequestScope[scope],
      });
      if (!response || !Array.isArray(response.conflicts)) {
        throw new Error('Task conflict preview response is required');
      }

      return response.conflicts;
    },
    [previewContractInformationTaskConflicts, requireResource, scope]
  );

  const performEnsure = useCallback(async (): Promise<ContractInformationSynchronizationResult> => {
    if (!enabled) return { status: 'ready' };

    const operationResourceKey = resourceKey;
    setState((current) =>
      current.resourceKey === operationResourceKey ? { ...current, status: 'ensuring' } : current
    );

    for (let attempt = 0; attempt <= transientRetryDelays.length; attempt += 1) {
      try {
        const resource = requireResource();
        const response = await ensureContractInformationTasks({
          ...resource,
          scope,
        });
        if (!response?.outcome) throw new Error('Task synchronization outcome is required');

        const isCoolingOffTaskCompleted = requireCoolingOffCompletion(response);
        if (response.outcome === EnsureContractInformationTasksResponseOutcome.Repaired) {
          await invalidateTasks(resource.campaignId, resource.contractId);
        }

        if (mounted.current && currentResourceKey.current === operationResourceKey) {
          setState({
            resourceKey: operationResourceKey,
            status: 'ready',
            isCoolingOffTaskCompleted,
          });
        }

        return { status: 'ready' };
      } catch (error) {
        if (isContractInformationTaskSyncError(error)) {
          const presentation = presentSynchronizationError(error, scope);
          if (mounted.current && currentResourceKey.current === operationResourceKey) {
            setState({
              resourceKey: operationResourceKey,
              status: presentation.result.status,
              issue: presentation.issue,
              isCoolingOffTaskCompleted: presentation.isCoolingOffTaskCompleted,
            });
          }

          return presentation.result;
        }

        if (isTransientError(error) && attempt < transientRetryDelays.length) {
          const delay = transientRetryDelays[attempt];
          if (delay > 0) await wait(delay);
          continue;
        }

        if (mounted.current && currentResourceKey.current === operationResourceKey) {
          setState({
            resourceKey: operationResourceKey,
            status: unverifiedResult.result.status,
            issue: unverifiedResult.issue,
          });
        }

        return unverifiedResult.result;
      }
    }

    return unverifiedResult.result;
  }, [
    enabled,
    ensureContractInformationTasks,
    invalidateTasks,
    requireResource,
    resourceKey,
    scope,
  ]);

  const startEnsure = useCallback(() => {
    const promise = performEnsure();
    queue.current.running = promise;
    void promise.finally(() => {
      if (queue.current.running === promise && !queue.current.trailing) {
        queue.current.running = undefined;
      }
    });
    return promise;
  }, [performEnsure]);

  const requestEnsure = useCallback(
    (fresh: boolean): Promise<ContractInformationSynchronizationResult> => {
      if (queue.current.resourceKey !== resourceKey) queue.current = { resourceKey };
      if (!queue.current.running) return startEnsure();
      if (!fresh) return queue.current.running;
      if (queue.current.trailing) return queue.current.trailing;

      const running = queue.current.running;
      const startTrailing = () => {
        if (queue.current.resourceKey !== resourceKey) return { status: 'ready' as const };

        queue.current.running = undefined;
        queue.current.trailing = undefined;
        return startEnsure();
      };
      queue.current.trailing = running.then(startTrailing, startTrailing);
      return queue.current.trailing;
    },
    [resourceKey, startEnsure]
  );
  useLayoutEffect(() => {
    requestEnsureRef.current = requestEnsure;
  }, [requestEnsure]);

  useEffect(() => {
    if (!enabled) return;

    void requestEnsureRef.current?.(true);
  }, [canonicalRevision, enabled, resourceKey]);

  useEffect(() => {
    if (!enabled || !campaignId || !contractId) return;

    return subscribeToTaskCollectionRefresh((notification) => {
      if (notification.campaignId !== campaignId || notification.contractId !== contractId) return;
      void requestEnsureRef.current?.(true);
    });
  }, [campaignId, contractId, enabled]);

  useEffect(() => {
    if (!enabled) return;

    return subscribeToTaskCollectionReconnect(() => {
      void requestEnsureRef.current?.(true);
    });
  }, [enabled]);

  const handleTaskUpdated = useCallback(
    (event: TaskUpdatedNotification) => {
      if (!enabled || event.task.campaignId !== campaignId || event.task.contractId !== contractId)
        return;

      void requestEnsureRef.current?.(true);
    },
    [campaignId, contractId, enabled]
  );
  useAutopilotTaskUpdates(handleTaskUpdated);

  const currentState = state.resourceKey === resourceKey ? state : undefined;
  const ensure = useCallback(() => requestEnsure(true), [requestEnsure]);

  return {
    previewConflicts,
    coolingOffControlsLocked: enabled && currentState?.isCoolingOffTaskCompleted !== false,
    isCoolingOffTaskCompleted: currentState?.isCoolingOffTaskCompleted === true,
    synchronization: enabled
      ? {
          ensure,
          retry: ensure,
          status: currentState?.status ?? 'ensuring',
          issue: currentState?.issue,
        }
      : undefined,
  };
};
